import React, { useState, useEffect } from "react";
import { Section, Trait } from "../../types";
import { useApp } from "../../context/AppContext";
import { getSections } from "../../services/sectionService";
import { getTraits } from "../../services/traitService";
import {
    getAssessmentTemplates,
    createAssessmentTemplate,
    updateAssessmentTemplate,
    deleteAssessmentTemplate,
    toggleTemplateStatus,
    duplicateTemplate,
    AssessmentTemplate,
    AssessmentTemplateForm
} from "../../services/orgAssessmentService";
import PageShell from "../../components/layout/PageShell";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Table from "../../components/ui/Table";
import Modal from "../../components/ui/Modal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import { Input, Select, Textarea, Toggle } from "../../components/ui/FormFields";
import { toast } from "react-hot-toast";

interface Props {
    orgId?: string;
}

const BLANK_TEMPLATE: AssessmentTemplateForm = {
    name: "",
    description: "",
    targetAudience: "all",
    sections: [],
    instructions: "",
    timeLimit: 0,
    isActive: true,
};

const AssessmentTemplatesPage: React.FC<Props> = ({ orgId }) => {
    const { currentUser } = useApp();
    const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);
    const [sections, setSections] = useState<Section[]>([]);
    const [traits, setTraits] = useState<Trait[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editTemplate, setEditTemplate] = useState<AssessmentTemplate | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<AssessmentTemplate | null>(null);
    const [viewTemplate, setViewTemplate] = useState<AssessmentTemplate | null>(null);
    const [audienceFilter, setAudienceFilter] = useState("all");
    const [form, setForm] = useState<AssessmentTemplateForm>(BLANK_TEMPLATE);

    // Helper function to get user ID (works with both 'id' and 'uid')
    const getUserId = (): string | null => {
        if (!currentUser) return null;
        // Try to get id from different possible property names
        return (currentUser as any).id || (currentUser as any).uid || null;
    };

    useEffect(() => {
        loadData();
    }, [currentUser]);

    const loadData = async () => {
        const userId = getUserId();

        if (!userId) {
            console.warn("No user logged in - userId:", userId, "currentUser:", currentUser);
            toast.error("Please login to view assessment templates");
            setLoading(false);
            return;
        }

        console.log("Loading templates for user:", userId);
        setLoading(true);
        try {
            const [fetchedTemplates, fetchedSections, fetchedTraits] = await Promise.all([
                getAssessmentTemplates(userId),
                getSections(),
                getTraits(),
            ]);
            console.log("Templates:", fetchedTemplates);
            console.log("Sections:", fetchedSections);
            console.log("Traits:", fetchedTraits);
            setTemplates(fetchedTemplates);
            setSections(fetchedSections);
            setTraits(fetchedTraits);
            console.log("Templates loaded:", fetchedTemplates.length);
        } catch (error) {
            console.error("Error loading data:", error);
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (field: keyof AssessmentTemplateForm, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const addSection = () => {
        setForm(prev => ({
            ...prev,
            sections: [
                ...prev.sections,
                {
                    sectionId: "",
                    sectionName: "",
                    traits: [],
                    questionSelectionRule: "random",
                    numberOfQuestions: 10,
                    order: prev.sections.length + 1,
                },
            ],
        }));
    };

    const updateSection = (index: number, field: string, value: any) => {
        const updatedSections = [...form.sections];
        updatedSections[index] = { ...updatedSections[index], [field]: value };

        if (field === "sectionId") {
            const selectedSection = sections.find(s => s.id === value);
            if (selectedSection) {
                updatedSections[index].sectionName = selectedSection.name;
                updatedSections[index].traits = [];
                updatedSections[index].numberOfQuestions = Math.min(
                    updatedSections[index].numberOfQuestions || 10,
                    selectedSection.totalQuestions || selectedSection.questionCount || 0
                );
            }
        }

        setForm(prev => ({ ...prev, sections: updatedSections }));
    };

    const removeSection = (index: number) => {
        setForm(prev => ({
            ...prev,
            sections: prev.sections.filter((_, i) => i !== index),
        }));
    };

    const handleTraitCheckboxChange = (sectionIndex: number, traitId: string, isChecked: boolean) => {
        const updatedSections = [...form.sections];
        const currentTraits = updatedSections[sectionIndex].traits || [];

        if (isChecked) {
            updatedSections[sectionIndex].traits = [...currentTraits, traitId];
        } else {
            updatedSections[sectionIndex].traits = currentTraits.filter(id => id !== traitId);
        }

        setForm(prev => ({ ...prev, sections: updatedSections }));
    };

    const getAvailableTraitsForSection = (sectionId: string) => {
        if (!sectionId) return [];
        const section = sections.find(s => s.id === sectionId);
        if (!section) return [];
        const sectionTraitIds = section.traits.map(t => t.traitId);
        return traits.filter(t => sectionTraitIds.includes(t.id));
    };

    const calculateTotals = () => {
        const totalQuestions = form.sections.reduce((sum, s) => sum + s.numberOfQuestions, 0);
        const totalDuration = form.timeLimit || form.sections.reduce((sum, s) => sum + (s.numberOfQuestions * 1.5), 0);
        return { totalQuestions, totalDuration };
    };

    const handleSave = async () => {
        const userId = getUserId();

        if (!userId) {
            toast.error("Please login to save assessment template");
            return;
        }

        if (!form.name.trim()) {
            toast.error("Template name is required");
            return;
        }

        if (form.sections.length === 0) {
            toast.error("Please add at least one section");
            return;
        }

        // Validate each section
        for (const section of form.sections) {
            if (!section.sectionId) {
                toast.error("Please select a section for all entries");
                return;
            }
            if (!section.traits || section.traits.length === 0) {
                const sectionName = sections.find(s => s.id === section.sectionId)?.name;
                toast.error(`Please select at least one trait for section: ${sectionName}`);
                return;
            }
        }

        try {
            if (editTemplate) {
                await updateAssessmentTemplate(editTemplate.id, form, userId);
                toast.success("Assessment template updated successfully");
            } else {
                const storedUser = JSON.parse(localStorage.getItem("currentUser") || "{}");

                await createAssessmentTemplate(
                    form,
                    storedUser.id || storedUser.uid || "system",
                    storedUser.organizationId || "",
                    storedUser.organizationName || "",
                    storedUser.role || ""
                );

                toast.success("Assessment template created successfully");
            }

            await loadData();
            setShowModal(false);
            resetForm();
        } catch (error) {
            console.error("Error saving template:", error);
            toast.error("Failed to save assessment template");
        }
    };

    const handleDuplicate = async (template: AssessmentTemplate) => {
        const userId = getUserId();

        if (!userId) {
            toast.error("Please login to duplicate template");
            return;
        }

        try {
            await duplicateTemplate(template.id, userId);
            toast.success("Template duplicated successfully");
            await loadData();
        } catch (error) {
            console.error("Error duplicating template:", error);
            toast.error(error instanceof Error ? error.message : "Failed to duplicate template");
        }
    };

    const handleToggleStatus = async (template: AssessmentTemplate) => {
        const userId = getUserId();

        if (!userId) {
            toast.error("Please login to change template status");
            return;
        }

        try {
            await toggleTemplateStatus(template.id, !template.isActive, userId);
            toast.success(`Template ${!template.isActive ? "activated" : "deactivated"} successfully`);
            await loadData();
        } catch (error) {
            console.error("Error toggling status:", error);
            toast.error(error instanceof Error ? error.message : "Failed to toggle template status");
        }
    };

    const handleDelete = async (template: AssessmentTemplate) => {
        const userId = getUserId();

        if (!userId) {
            toast.error("Please login to delete template");
            return;
        }

        try {
            await deleteAssessmentTemplate(template.id, userId);
            toast.success("Template deleted successfully");
            await loadData();
            setDeleteTarget(null);
        } catch (error) {
            console.error("Error deleting template:", error);
            toast.error(error instanceof Error ? error.message : "Failed to delete template");
        }
    };

    const resetForm = () => {
        setForm(JSON.parse(JSON.stringify(BLANK_TEMPLATE)));
        setEditTemplate(null);
    };

    const openNew = () => {
        resetForm();
        setShowModal(true);
    };

    const openEdit = (template: AssessmentTemplate) => {
        setEditTemplate(template);
        setForm({
            name: template.name,
            description: template.description || "",
            targetAudience: template.targetAudience,
            sections: template.sections.map(s => ({
                sectionId: s.sectionId,
                sectionName: s.sectionName,
                traits: [...(s.traits || [])],
                questionSelectionRule: s.questionSelectionRule,
                numberOfQuestions: s.numberOfQuestions,
                order: s.order
            })),
            instructions: template.instructions || "",
            timeLimit: template.timeLimit || 0,
            isActive: template.isActive,
        });
        setShowModal(true);
    };

    const { totalQuestions, totalDuration } = calculateTotals();
    const filteredTemplates = audienceFilter === "all"
        ? templates
        : templates.filter(t => t.targetAudience === audienceFilter);

    const audienceOptions = [
        { value: "all", label: "All Audiences" },
        { value: "student", label: "🎓 Students" },
        { value: "employee", label: "💼 Employees" },
        { value: "leader", label: "👔 Leaders" },
    ];

    const targetAudienceOptions = [
        { value: "student", label: "🎓 Students" },
        { value: "employee", label: "💼 Employees" },
        { value: "leader", label: "👔 Leaders" },
        { value: "all", label: "🌍 All Audiences" },
    ];

    const selectionRuleOptions = [
        { value: "random", label: "🎲 Random (Pick random questions from bank)" },
        { value: "fixed", label: "📌 Fixed (Use specific questions)" },
    ];

    // Show loading state while checking authentication
    if (!currentUser) {
        return (
            <PageShell title="Assessment Templates" subtitle="Please login to view templates">
                <GlassCard className="p-8 text-center">
                    <div className="text-gray-500">Please login to access assessment templates.</div>
                </GlassCard>
            </PageShell>
        );
    }

    return (
        <PageShell
            title="Assessment Templates"
            subtitle="Create reusable assessment templates by combining sections, traits, and question selection rules"
            action={{ label: "New Template", onClick: openNew }}
        >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <StatCard label="Total Templates" value={templates.length} icon="📋" color="#6366f1" />
                <StatCard label="Active Templates" value={templates.filter(t => t.isActive).length} icon="✅" color="#10b981" />
                <StatCard label="Avg Questions" value={Math.round(templates.reduce((sum, t) => sum + t.totalQuestions, 0) / (templates.length || 1))} icon="❓" color="#f59e0b" />
                <StatCard label="Avg Duration" value={`${Math.round(templates.reduce((sum, t) => sum + t.totalDuration, 0) / (templates.length || 1))} min`} icon="⏱️" color="#8b5cf6" />
            </div>

            <GlassCard className="p-4 mb-6">
                <div className="flex items-start gap-3">
                    <span className="text-2xl">📐</span>
                    <div>
                        <p className="text-sm font-bold text-gray-700">Assessment Template Guide</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Templates allow you to create standardized assessments. Each template combines multiple sections,
                            each with specific traits. Questions can be randomly selected or fixed from the question bank.
                        </p>
                    </div>
                </div>
            </GlassCard>

            <div className="flex gap-2 flex-wrap mb-4">
                {audienceOptions.map(opt => (
                    <button
                        key={opt.value}
                        onClick={() => setAudienceFilter(opt.value)}
                        className={`px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${audienceFilter === opt.value
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : "bg-white/50 text-gray-600 border-gray-200 hover:bg-white/80"
                            }`}
                    >
                        {opt.label}
                    </button>
                ))}
            </div>

            <GlassCard className="p-5">
                <Table
                    columns={[
                        {
                            key: "name",
                            label: "Template Name",
                            render: (v, item) => (
                                <div>
                                    <span className="font-semibold text-gray-800">{v}</span>
                                    {item.isActive && (
                                        <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
                                    )}
                                </div>
                            )
                        },
                        {
                            key: "targetAudience",
                            label: "Audience",
                            render: (v) => (
                                <Badge
                                    text={v}
                                    variant={v === "student" ? "blue" : v === "employee" ? "green" : v === "leader" ? "purple" : "default"}
                                />
                            )
                        },
                        { key: "sections", label: "Sections", render: (v) => v.length },
                        { key: "totalQuestions", label: "Questions", render: (v) => v },
                        { key: "totalDuration", label: "Duration", render: (v) => `${Math.round(v)} min` },
                        { key: "createdByName", label: "Created By", render: (v) => v },
                        { key: "createdAt", label: "Created", render: (v) => new Date(v).toLocaleDateString() },
                        {
                            key: "actions",
                            label: "Actions",
                            render: (_, item) => (
                                <div className="flex gap-1">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setViewTemplate(item);
                                        }}
                                        className="px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 transition-colors"
                                    >
                                        View
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDuplicate(item);
                                        }}
                                        className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                                    >
                                        Duplicate
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleToggleStatus(item);
                                        }}
                                        className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${item.isActive
                                            ? "text-orange-600 bg-orange-50 hover:bg-orange-100"
                                            : "text-green-600 bg-green-50 hover:bg-green-100"
                                            }`}
                                    >
                                        {item.isActive ? "Deactivate" : "Activate"}
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteTarget(item);
                                        }}
                                        className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors"
                                    >
                                        Delete
                                    </button>
                                </div>
                            )
                        },
                    ]}
                    data={filteredTemplates}
                    onEdit={openEdit}
                    onDelete={(t) => setDeleteTarget(t)}
                />
                {templates.length === 0 && !loading && (
                    <div className="text-center py-8 text-gray-500">
                        No assessment templates found. Click "New Template" to create your first template.
                    </div>
                )}
            </GlassCard>

            {/* View Template Modal */}
            <Modal isOpen={!!viewTemplate} onClose={() => setViewTemplate(null)} title="Template Details" size="lg">
                {viewTemplate && (
                    <div className="space-y-5">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                ["Audience", viewTemplate.targetAudience],
                                ["Sections", viewTemplate.sections.length],
                                ["Questions", viewTemplate.totalQuestions],
                                ["Duration", `${Math.round(viewTemplate.totalDuration)} min`],
                            ].map(([l, v]) => (
                                <GlassCard key={l} className="p-3 text-center">
                                    <p className="text-xs text-gray-400">{l}</p>
                                    <p className="font-extrabold text-gray-800 mt-0.5 capitalize">{String(v)}</p>
                                </GlassCard>
                            ))}
                        </div>

                        <div>
                            <p className="text-sm font-bold text-gray-600 mb-2">Sections & Traits</p>
                            {viewTemplate.sections.map((section, idx) => {
                                const sectionTraits = traits.filter(t => section.traits?.includes(t.id));
                                return (
                                    <div key={idx} className="mb-4 p-3 bg-gray-50 rounded-lg">
                                        <p className="font-semibold text-gray-800">{section.sectionName}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {section.questionSelectionRule === "random" ? "🎲 Random" : "📌 Fixed"} · {section.numberOfQuestions} questions
                                        </p>
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {sectionTraits.length > 0 ? (
                                                sectionTraits.map(trait => (
                                                    <span key={trait.id} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                                        {trait.name}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-xs text-gray-400">No traits selected</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {viewTemplate.instructions && (
                            <div>
                                <p className="text-sm font-bold text-gray-600 mb-1">Instructions</p>
                                <p className="text-sm text-gray-500">{viewTemplate.instructions}</p>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            {/* Create/Edit Template Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => { setShowModal(false); resetForm(); }}
                title={editTemplate ? "Edit Assessment Template" : "Create Assessment Template"}
                size="xl"
            >
                <div className="space-y-5 max-h-[80vh] overflow-y-auto px-1">
                    {/* Basic Information Section */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <span className="text-xl">📝</span>
                                Basic Information
                            </h3>
                        </div>
                        <div className="p-5 space-y-4">
                            <Input
                                label="Template Name"
                                value={form.name}
                                onChange={(v) => handleFormChange("name", v)}
                                required
                                placeholder="e.g., Student Psychometric Assessment"
                            />
                            <Textarea
                                label="Description"
                                value={form.description}
                                onChange={(v) => handleFormChange("description", v)}
                                placeholder="Describe the purpose and use case of this assessment..."
                                rows={2}
                            />
                            <Select
                                label="Target Audience"
                                value={form.targetAudience}
                                onChange={(v) => handleFormChange("targetAudience", v as any)}
                                options={targetAudienceOptions}
                            />
                            <Textarea
                                label="Assessment Instructions"
                                value={form.instructions}
                                onChange={(v) => handleFormChange("instructions", v)}
                                placeholder="Instructions that will be shown to candidates before starting the assessment..."
                                rows={3}
                            />
                            <Input
                                label="Time Limit (minutes)"
                                type="number"
                                value={String(form.timeLimit)}
                                onChange={(v) => handleFormChange("timeLimit", Number(v))}
                                placeholder="0 for no time limit"
                            />
                            <Toggle
                                checked={form.isActive}
                                onChange={(v) => handleFormChange("isActive", v)}
                                label="Active (available for assignment)"
                            />
                        </div>
                    </div>

                    {/* Sections Configuration */}
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-5 py-3 border-b border-gray-200">
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <span className="text-xl">📂</span>
                                Sections & Traits Configuration
                            </h3>
                        </div>
                        <div className="p-5">
                            {form.sections.map((section, idx) => {
                                const availableTraits = getAvailableTraitsForSection(section.sectionId);
                                const selectedSection = sections.find(s => s.id === section.sectionId);
                                const currentSelectedTraits = section.traits || [];

                                return (
                                    <div key={idx} className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                        <div className="flex justify-between items-start mb-3">
                                            <h4 className="font-semibold text-gray-700">Section {idx + 1}</h4>
                                            <Button
                                                size="sm"
                                                variant="danger"
                                                onClick={() => removeSection(idx)}
                                                className="text-xs"
                                            >
                                                Remove
                                            </Button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                            <Select
                                                label="Select Section"
                                                value={section.sectionId}
                                                onChange={(v) => updateSection(idx, "sectionId", v)}
                                                options={[
                                                    { value: "", label: "-- Select Section --" },
                                                    ...sections.map(s => ({ value: s.id, label: `${s.name} (${s.questionCount} Qs, ${s.duration} min)` }))
                                                ]}
                                            />
                                            <Select
                                                label="Question Selection Rule"
                                                value={section.questionSelectionRule}
                                                onChange={(v) => updateSection(idx, "questionSelectionRule", v)}
                                                options={selectionRuleOptions}
                                            />
                                            <Input
                                                label="Number of Questions"
                                                type="number"
                                                value={String(section.numberOfQuestions)}
                                                onChange={(v) => updateSection(idx, "numberOfQuestions", Number(v))}
                                                min="1"
                                                max={selectedSection?.questionCount || 100}
                                            />
                                        </div>

                                        {section.sectionId && (
                                            <div className="mt-3">
                                                <label className="text-sm font-medium text-gray-700 mb-2 block">
                                                    Select Traits to Include ({availableTraits.length} available)
                                                </label>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-white">
                                                    {availableTraits.length === 0 ? (
                                                        <p className="text-gray-500 text-sm col-span-2 text-center py-4">
                                                            No traits available for this section. Please check the section configuration.
                                                        </p>
                                                    ) : (
                                                        availableTraits.map(trait => {
                                                            const isChecked = currentSelectedTraits.includes(trait.id);
                                                            return (
                                                                <div key={trait.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded">
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`trait-${idx}-${trait.id}`}
                                                                        checked={isChecked}
                                                                        onChange={(e) => handleTraitCheckboxChange(idx, trait.id, e.target.checked)}
                                                                        className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 cursor-pointer"
                                                                    />
                                                                    <label
                                                                        htmlFor={`trait-${idx}-${trait.id}`}
                                                                        className="flex items-center gap-2 flex-1 cursor-pointer"
                                                                    >
                                                                        <span className="text-xl">{"🧠"}</span>
                                                                        <div className="flex-1">
                                                                            <span className="text-sm font-medium text-gray-700">{trait.name}</span>
                                                                        </div>
                                                                    </label>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                                {currentSelectedTraits.length > 0 && (
                                                    <div className="mt-2 p-2 bg-green-50 rounded-lg">
                                                        <p className="text-xs text-green-700">
                                                            ✓ {currentSelectedTraits.length} trait(s) selected for this section
                                                        </p>
                                                    </div>
                                                )}
                                                {availableTraits.length > 0 && currentSelectedTraits.length === 0 && (
                                                    <p className="text-xs text-yellow-600 mt-2">
                                                        ⚠️ Please select at least one trait for this section
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            <Button onClick={addSection} variant="secondary" className="w-full">
                                + Add Section
                            </Button>
                        </div>
                    </div>

                    {/* Assessment Summary */}
                    {form.sections.length > 0 && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-5 py-3 border-b border-blue-200">
                                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                    <span className="text-xl">📊</span>
                                    Assessment Summary
                                </h3>
                            </div>
                            <div className="p-5">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-indigo-600">{totalQuestions}</p>
                                        <p className="text-xs text-gray-500">Total Questions</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-2xl font-bold text-indigo-600">{Math.round(totalDuration)} min</p>
                                        <p className="text-xs text-gray-500">Estimated Duration</p>
                                    </div>
                                </div>
                                <div className="mt-3 p-3 bg-white rounded-lg">
                                    <p className="text-xs font-medium text-gray-600 mb-2">Sections & Selected Traits:</p>
                                    <div className="space-y-2">
                                        {form.sections.map((s, idx) => {
                                            const sectionInfo = sections.find(sect => sect.id === s.sectionId);
                                            const selectedTraitsList = traits.filter(t => s.traits?.includes(t.id));
                                            return (
                                                <div key={idx} className="border-b border-gray-100 pb-2">
                                                    <p className="text-sm font-semibold text-gray-700">
                                                        {idx + 1}. {sectionInfo?.name || s.sectionName || "Unnamed Section"}
                                                    </p>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {selectedTraitsList.length > 0 ? (
                                                            selectedTraitsList.map(trait => (
                                                                <span key={trait.id} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                                                    {trait.name}
                                                                </span>
                                                            ))
                                                        ) : (
                                                            <span className="text-xs text-gray-400">No traits selected</span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        {s.questionSelectionRule === "random" ? "🎲 Random" : "📌 Fixed"} · {s.numberOfQuestions} questions
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 sticky bottom-0 bg-white py-4">
                        <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}>Cancel</Button>
                        <Button onClick={handleSave} className="bg-gradient-to-r from-indigo-600 to-purple-600">
                            {editTemplate ? "Update Template" : "Create Template"}
                        </Button>
                    </div>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
                title="Delete Template"
                message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
            />
        </PageShell>
    );
};

export default AssessmentTemplatesPage;